import {
  BasicInstance,
  ConnectableElement,
  ConnectorInstance,
  Datapoint,
  DefaultDatapointTypes,
  InlineConnectableElement,
  InlineDatapoint,
  WindowMessageDatapoint,
  WindowMessageEnvelope
} from '@olive/olive-types';
import ConnectivityHolder from '../ConnectivityHolder';
import ConnectivityManager from '../ConnectivityManager';
import AbstractDispatcher from './AbstractDispatcher';

const REGISTER_ORIGIN_TOPIC = 'oliveRegisterOrigin'
const ACKKNOWLEDGE_ORIGIN_TOPIC = 'oliveAcknowledgeOrigin'

export default class WindowDispatcher extends AbstractDispatcher {
  connectivityManager: ConnectivityManager;
  origins: {
    [origin: string]: {
      origin: string,
      win: MessageEventSource
    }
  }

  constructor(
    connectivityHolder: ConnectivityHolder,
    connectivityManager: ConnectivityManager
  ) {
    super(connectivityHolder);
    this.connectivityManager = connectivityManager;
    this.origins = {};
    window.addEventListener('message', this.#onMessage.bind(this));
  }

  async #onMessage(message: MessageEvent) {
    console.log('#onMessage -- ', message);
    const envelope = message.data as WindowMessageEnvelope;

    if (this.#isRegisterOriginMessage(envelope)) {
      this.#registerOriginMessage(message);
      return;
    }

    console.log('event to dispatch', envelope);

    const allConnectivitys = this.connectivityHolder.getAll();
    if (!allConnectivitys) {
      return;
    }
    const connectors = Object.keys(allConnectivitys).map(key => allConnectivitys[key]).filter(connectivity => connectivity.type === 'connectorInstance');


    const matchingConnectors = connectors.filter(connector => {
      const connectorInstance = connector as ConnectorInstance;
      const datapointID = envelope.datapointID && connectorInstance.elements?.[0] && (connectorInstance.elements?.[0] as BasicInstance)?.instanceID === envelope.datapointID;
      if (datapointID) {
        return true;
      }
      const firstElement = connectorInstance.elements?.[0] as BasicInstance;
      if (firstElement?.instanceID) {
        const firstElementDatapoint = this.connectivityHolder.get(firstElement.instanceID);
        if (firstElementDatapoint?.type === 'datapoint' && firstElementDatapoint.datapoint.type === 'windowmessage') {
          return (firstElementDatapoint as WindowMessageDatapoint).datapoint.windowmessage.topic === envelope.topic;
        }
      }

      return false;
    });
    console.log('matchingConnectors', matchingConnectors);

    const dispatchers = matchingConnectors.map(connector => this.connectivityManager.invoke({
      id: connector.instanceID || connector.instanceOf,
      input: envelope.payload
    }));
    const result = await Promise.all(dispatchers);
    console.log('dispatched: ', result);
  }

  #isRegisterOriginMessage(envelope: WindowMessageEnvelope) {
    return envelope.topic === REGISTER_ORIGIN_TOPIC;
  }

  #registerOriginMessage(message: MessageEvent) {
    console.info('registering new window origin');
    const envelope = message.data as WindowMessageEnvelope;
    const payload = envelope.payload as unknown as { id: string, origin: string };
    if (payload.origin && payload.id) {
      this.origins[payload.id] = {
        origin: payload.origin,
        win: message.source
      }
      this.#acknowldegeOriginMessage(payload.id);
    }
    else {
      console.error('Missing origin or window in register origin message: ', payload);
    }
  }

  #acknowldegeOriginMessage(originID: string) {
    this.origins[originID].win.postMessage({
      topic: ACKKNOWLEDGE_ORIGIN_TOPIC
    }, this.origins[originID].origin as WindowPostMessageOptions);
  }

  #getDatapoint(id: string | undefined | null): Datapoint | null {
    if (!id) {
      return null;
    }
    return this.connectivityHolder.get(id) as Datapoint;
  }

  canDispatch(props: { id: string, datapoint: Datapoint | undefined }): boolean {
    const datapoint = !props.datapoint ? this.#getDatapoint(props.id) : props.datapoint;
    if (!datapoint) {
      return false;
    }
    const type = datapoint.datapoint.type;
    return type === DefaultDatapointTypes.WINDOW_MESSAGE;
  }

  async dispatch({id, task, input}: { id: string, task?: ConnectableElement | InlineConnectableElement, input?: object }) {

    let datapoint: WindowMessageDatapoint;
    if(task && task.type === 'datapoint' && 'datapoint' in task && (task.datapoint as InlineDatapoint).type === 'windowmessage') {
      datapoint = task as WindowMessageDatapoint;
    }
    else
    {
      datapoint = this.#getDatapoint(id) as WindowMessageDatapoint;
    }
    if (!datapoint || datapoint.datapoint?.type !== 'windowmessage') {
      console.warn('Datapoint not found or not of type windowmessage: ' + id);
      return null;
    }
    const windowMessage = datapoint.datapoint.windowmessage;
    const payload = windowMessage.payload;

    const origin = windowMessage.origin;
    const originEntry = this.origins[origin];
    if (!originEntry) {
      console.error('Tried to dispatch message to unknown origin: ' + origin);
    }

    const topic = windowMessage.topic;

    const envelope = {
      topic,
      payload
    };

    //@ts-expect-error False positive
    originEntry.win.postMessage(envelope, originEntry.origin);

    return input;
  }
}